import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Filter, Search, Star, TrendingUp, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { useSubscription } from '../contexts/SubscriptionContext.js';
import MonthlyPackageCard from '../components/MonthlyPackageCard.js';
import LoadingSpinner from '../components/common/LoadingSpinner.js';

// FORCE FRESH DEPLOYMENT - Backend static file fix deployed
const MonthlyPackages = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  
  const [packages, setPackages] = useState([]);
  const [currentPackage, setCurrentPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, current, popular, recent

  const userTier = subscription?.tier || 'basic';

  // Get the API base URL
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://elderfit.onrender.com/api';

  useEffect(() => {
    // Only fetch packages if user is not on basic tier
    if (userTier !== 'basic') {
      fetchPackages();
      fetchCurrentPackage();
    } else {
      setLoading(false);
    }
  }, [userTier]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      console.log('Fetching packages from:', `${apiBaseUrl}/monthly-packages`);
      const response = await fetch(`${apiBaseUrl}/monthly-packages?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch packages');
      }

      const data = await response.json();
      setPackages(data.packages || []);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError('Failed to load monthly packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentPackage = async () => {
    try {
      console.log('Fetching current package from:', `${apiBaseUrl}/monthly-packages/current/month`);
      const response = await fetch(`${apiBaseUrl}/monthly-packages/current/month?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentPackage(data);
      }
    } catch (err) {
      console.error('Error fetching current package:', err);
    }
  };

  const handleViewPackage = (packageId) => {
    navigate(`/monthly-packages/${packageId}`);
  };

  const handleUpgrade = () => {
    navigate('/subscription/plans');
  };

  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.theme.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' ||
                         (filter === 'current' && pkg.status === 'published') ||
                         (filter === 'popular' && pkg.analytics?.views > 100) ||
                         (filter === 'recent' && new Date(pkg.releaseDate) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
    
    return matchesSearch && matchesFilter;
  });

  const getTierBenefits = () => {
    const benefits = {
      basic: ['Exercise images', 'Step-by-step instructions', 'Basic workout tracking'],
      premium: ['Everything in Basic', 'Short explainer videos', 'Exercise modifications', 'Advanced tracking'],
      elite: ['Everything in Premium', 'Full guided workout videos with professional trainers', 'Family monitoring', 'AI-powered training']
    };
    return benefits[userTier] || benefits.basic;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Packages</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show upgrade message for basic tier users
  if (userTier === 'basic') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Monthly Packages
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Unlock premium monthly workout packages with exclusive content, 
              workout of the week challenges, and personalized fitness programs.
            </p>
            
            <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl mx-auto mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                What's Included in Monthly Packages:
              </h2>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="space-y-2">
                  <div className="flex items-center text-gray-700">
                    <span className="text-green-500 mr-2">✓</span>
                    Workout of the Week (WOW)
                  </div>
                  <div className="flex items-center text-gray-700">
                    <span className="text-green-500 mr-2">✓</span>
                    Monthly Challenges
                  </div>
                  <div className="flex items-center text-gray-700">
                    <span className="text-green-500 mr-2">✓</span>
                    Premium Video Content
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center text-gray-700">
                    <span className="text-green-500 mr-2">✓</span>
                    Progress Tracking
                  </div>
                  <div className="flex items-center text-gray-700">
                    <span className="text-green-500 mr-2">✓</span>
                    Community Features
                  </div>
                  <div className="flex items-center text-gray-700">
                    <span className="text-green-500 mr-2">✓</span>
                    Expert Guidance
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleUpgrade}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Upgrade to Premium
              </button>
              <p className="text-sm text-gray-500">
                Start your fitness journey with premium monthly content
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Monthly Packages
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover premium monthly workout packages designed to challenge and inspire your fitness journey.
          </p>
        </div>

        {/* Current Package Highlight */}
        {currentPackage && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-md p-6 text-white mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">
                  Your Current Package
                </h2>
                <p className="text-blue-100 mb-4">
                  {currentPackage.title}
                </p>
                <div className="flex items-center space-x-4 text-sm">
                  <span>Month {currentPackage.subscriptionMonth}</span>
                  <span>•</span>
                  <span>{currentPackage.workoutOfTheWeek?.length || 0} weekly workouts</span>
                  <span>•</span>
                  <span>{currentPackage.estimatedDuration} minutes</span>
                </div>
              </div>
              <button
                onClick={() => handleViewPackage(currentPackage._id)}
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              >
                View Package
              </button>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search packages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('current')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'current'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-1" />
              Current
            </button>
            <button
              onClick={() => setFilter('popular')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'popular'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <TrendingUp className="w-4 h-4 inline mr-1" />
              Popular
            </button>
          </div>
        </div>

        {/* Packages Grid */}
        {filteredPackages.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No packages found
            </h3>
            <p className="text-gray-600">
              {searchTerm ? 'Try adjusting your search terms.' : 'Check back soon for new monthly packages!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPackages.map((pkg) => (
              <MonthlyPackageCard
                key={pkg._id}
                package={pkg}
                onViewPackage={handleViewPackage}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyPackages;